/**
 * Where the selected aircraft has already been this sortie.
 *
 * The live breadcrumb in useDroneMarkers only holds what this browser has
 * watched — 600 fixes, about a minute — so a commander who opens the screen
 * mid-flight sees an aircraft with no past. Everything before the tab was
 * opened is on the server in the flight log, and reading it is what turns
 * "where is it" into "where has it been".
 *
 * Only the ACTIVE session is drawn. The flight log will happily hand back the
 * last completed sortie too, and a finished flight's track drawn on a live COP
 * is a line an operator can read as current. No active session means no line.
 *
 * One aircraft — the primary selection — not the whole fleet: a full track is
 * thousands of fixes, and ten of them fetched and redrawn would cost more than
 * the picture is worth. The line is dashed and dimmer than the live breadcrumb
 * so history never reads as the last minute of flight.
 */

import { useEffect, useRef } from 'react';

import { getFlightTelemetry, listFlights, type TelemetryPoint } from '@/api/flights';
import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import { trackColorFor } from '@/map/icons';
import { usePrimarySelectedId } from '@/stores/droneStore';

export const SORTIE_TRACK_SOURCE_ID = 'sortie-track';
export const SORTIE_TRACK_LINE_LAYER = 'sortie-track-line';
export const SORTIE_TRACK_START_LAYER = 'sortie-track-start';

/**
 * How many fixes are drawn. A half-hour sortie logged at 4 Hz is 7,000 points,
 * and past a couple of thousand the extra vertices are sub-pixel at any zoom a
 * whole flight fits on — they cost serialisation, not detail.
 */
const MAX_DRAWN_POINTS = 1500;

/** How often the track is re-read while the aircraft keeps flying. */
const REFRESH_MS = 30_000;

const EMPTY = { type: 'FeatureCollection', features: [] as unknown[] };

export type TrackLine = Array<[number, number]>;

/** A reported 0,0 is not a position — same rule the marker layer applies. */
function hasFix(point: TelemetryPoint): boolean {
  return (
    point.lat !== null && point.lon !== null && !(point.lat === 0 && point.lon === 0)
  );
}

/**
 * The drawable line for a session's telemetry, decimated to MAX_DRAWN_POINTS.
 * The last fix is always kept: it is the join between the recorded track and
 * the live breadcrumb, and dropping it leaves a visible gap behind the aircraft.
 */
export function trackLine(points: TelemetryPoint[]): TrackLine {
  const fixes = points.filter(hasFix);
  if (fixes.length === 0) return [];
  const stride = Math.max(1, Math.ceil(fixes.length / MAX_DRAWN_POINTS));
  const line: TrackLine = [];
  for (let i = 0; i < fixes.length; i += stride) {
    const point = fixes[i]!;
    line.push([point.lon as number, point.lat as number]);
  }
  const last = fixes[fixes.length - 1]!;
  const tail: [number, number] = [last.lon as number, last.lat as number];
  const drawnLast = line[line.length - 1];
  if (!drawnLast || drawnLast[0] !== tail[0] || drawnLast[1] !== tail[1]) line.push(tail);
  return line;
}

/** The line plus a launch dot, so "it started there" is legible without a label. */
export function sortieTrackFeatures(line: TrackLine, color: string): unknown[] {
  if (line.length < 2) return [];
  return [
    {
      type: 'Feature',
      properties: { color },
      geometry: { type: 'LineString', coordinates: line },
    },
    {
      type: 'Feature',
      properties: { color },
      geometry: { type: 'Point', coordinates: line[0] },
    },
  ];
}

export interface SortieTrackLayer {
  setTrack: (line: TrackLine, color: string) => void;
  destroy: () => void;
  stats: () => { redraws: number; points: number };
}

export function createSortieTrackLayer(map: MapLike): SortieTrackLayer {
  let redraws = 0;
  let drawn = 0;

  if (!map.getSource(SORTIE_TRACK_SOURCE_ID)) {
    map.addSource(SORTIE_TRACK_SOURCE_ID, { type: 'geojson', data: EMPTY });
  }

  if (!map.getLayer(SORTIE_TRACK_LINE_LAYER)) {
    map.addLayer({
      id: SORTIE_TRACK_LINE_LAYER,
      type: 'line',
      source: SORTIE_TRACK_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'LineString'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      // Dashed and half the opacity of the live breadcrumb: this is where the
      // aircraft has been, not where it is going or where it just was.
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 1.6,
        'line-opacity': 0.4,
        'line-dasharray': [3, 2],
      },
    });
  }

  if (!map.getLayer(SORTIE_TRACK_START_LAYER)) {
    map.addLayer({
      id: SORTIE_TRACK_START_LAYER,
      type: 'circle',
      source: SORTIE_TRACK_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': 4,
        'circle-color': 'rgba(0,0,0,0.45)',
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': 1.6,
        'circle-opacity': 0.7,
      },
    });
  }

  return {
    setTrack: (line, color) => {
      const source = geoJsonSource(map, SORTIE_TRACK_SOURCE_ID);
      if (!source) return;
      const features = sortieTrackFeatures(line, color);
      source.setData({ type: 'FeatureCollection', features });
      drawn = line.length;
      redraws += 1;
    },
    destroy: () => {
      for (const id of [SORTIE_TRACK_START_LAYER, SORTIE_TRACK_LINE_LAYER]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(SORTIE_TRACK_SOURCE_ID)) map.removeSource(SORTIE_TRACK_SOURCE_ID);
    },
    stats: () => ({ redraws, points: drawn }),
  };
}

/** Mount the layer. Call it from the screen, alongside the other layer hooks. */
export function useSortieTrackLayer(): void {
  const controller = useMapController();
  const droneId = usePrimarySelectedId();
  const layerRef = useRef<SortieTrackLayer | null>(null);
  const lineRef = useRef<TrackLine>([]);
  const colorRef = useRef<string>('#ffffff');

  // The layer and the fetch settle in either order — a slow map with cached
  // telemetry, or the reverse — so both write through this rather than one
  // assuming the other has already run.
  const draw = (): void => layerRef.current?.setTrack(lineRef.current, colorRef.current);

  useEffect(
    () =>
      controller.ready((map) => {
        layerRef.current = createSortieTrackLayer(map);
        draw();
        return () => {
          layerRef.current?.destroy();
          layerRef.current = null;
        };
      }),
    [controller],
  );

  useEffect(() => {
    lineRef.current = [];
    draw();
    if (!droneId) return undefined;
    colorRef.current = trackColorFor(droneId);

    let cancelled = false;
    const load = async (): Promise<void> => {
      const flights = await listFlights({ droneId, limit: 1 });
      if (cancelled || !flights.ok) return;
      const sessionId = flights.data.activeByDrone[droneId];
      if (!sessionId) {
        lineRef.current = [];
        draw();
        return;
      }
      const telemetry = await getFlightTelemetry(sessionId);
      if (cancelled || !telemetry.ok) return;
      lineRef.current = trackLine(telemetry.data);
      draw();
    };

    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [droneId]);
}
